import { Card } from '../components/ui/Card'
import { CardHeader } from '../components/ui/Card'
import { api } from '../utils/api'
import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'

/* ------------------------------------------------------------------ */
/*  PowerPage — PDU grid + phase analysis + capacity report           */
/* ------------------------------------------------------------------ */

interface PDU {
  id: string
  name: string
  model?: string
  manufacturer?: string
  rated_current_a: number
  num_outlets: number
  phase: string
  status: string
}

interface PowerFeed {
  id: string
  pdu_id: string
  outlet_number: number
  device_type: string
  connected_device_id?: string
  amperage_draw?: number
  status: string
}

interface RackPowerItem {
  rack_id: number
  label: string
  row_index: number
  rack_index: number
  consumption_watts: number
  max_power_w: number
}

interface CapacityReport {
  dc_code: string
  total_racks: number
  racks_with_power: number
  total_capacity_watts: number
  total_consumption_watts: number
  headroom_watts: number
  headroom_pct: number
  phase_imbalance: Record<string, number>
  rack_consumption?: Record<string, number>
  rack_breakdown?: RackPowerItem[]
}

interface SPOFIssue {
  severity: 'critical' | 'warning' | 'info'
  category: string
  description: string
  affected: string[]
}

interface SPOFAnalysis {
  dc_code: string
  issues: SPOFIssue[]
  summary: Record<string, unknown>
}

function PduVisual({ pdu, feeds }: { pdu: PDU; feeds: PowerFeed[] }) {
  const outlets = Array.from({ length: pdu.num_outlets }, (_, i) => feeds.find(f => f.outlet_number === i + 1) || null)
  const drawn = outlets.filter(Boolean).length
  const pct = Math.round(drawn / pdu.num_outlets * 100)
  const phaseColor: Record<string, string> = {
    phase_a: '#ef4444',
    phase_b: '#3b82f6',
    phase_c: '#f59e0b',
  }
  const color = phaseColor[pdu.phase] || '#6b7280'

  return (
    <div className="bg-bg-panel border border-border rounded-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{pdu.name}</span>
        <span className="text-xs font-mono" style={{ color }}>
          {pdu.phase.toUpperCase()}
        </span>
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {outlets.map((feed, i) => {
          const ampText = feed?.amperage_draw ? ` (${feed.amperage_draw}A)` : ''
          const titleText = feed ? `#${i + 1}: ${feed.device_type}${ampText}` : `#${i + 1}: свободен`
          return (
            <div
              key={i}
              className={`w-5 h-5 rounded-sm flex items-center justify-center text-[8px] ${
                feed ? 'bg-status-normal text-white' : 'bg-bg-input'
              }`}
              title={titleText}
            >
              {i + 1}
            </div>
          )
        })}
      </div>
      <div className="flex items-center justify-between text-xs text-text-muted">
        <span>{drawn}/{pdu.num_outlets} подключено</span>
        <span>{pct}%</span>
      </div>
    </div>
  )
}

export default function PowerPage() {
  const { dcCode } = useParams<{ dcCode: string }>()
  const [pdus, setPdus] = useState<PDU[]>([])
  const [dcList, setDcList] = useState<Array<{ code: string; name: string }>>([])
  const [feederMap, setFeederMap] = useState<Record<string, PowerFeed[]>>({})
  const [capacity, setCapacity] = useState<CapacityReport | null>(null)
  const [spof, setSpof] = useState<SPOFAnalysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [code, setCode] = useState(dcCode || '')

  useEffect(() => {
    if (dcCode) {
      setCode(dcCode)
    }
  }, [dcCode])

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      // Load DCs first
      const dcsRes = await api.get('/api/datacenters')
      const dcs = dcsRes.data as Array<{ code: string; name: string }>
      setDcList(dcs)
      if (dcs.length && !code) setCode(dcs[0].code)

      if (!code) { setLoading(false); return }

      const [powerRes, spofRes] = await Promise.allSettled([
        api.get(`/api/datacenters/${code}/power`),
        api.get(`/api/datacenters/${code}/spof-analysis`),
      ])

      if (powerRes.status === 'fulfilled') {
        const pdusList = powerRes.value.data.pdus || []
        setLoadError(null)
        setPdus(pdusList)
        setCapacity(powerRes.value.data.capacity || null)
        
        const feedResults = await Promise.all(
          pdusList.map((pdu: PDU) =>
            api.get(`/api/pdus/${pdu.id}/feeds`)
              .then(res => ({ id: pdu.id, feeds: res.data as PowerFeed[] }))
              .catch(() => ({ id: pdu.id, feeds: [] as PowerFeed[] }))
          )
        )
        const feeds: Record<string, PowerFeed[]> = {}
        for (const item of feedResults) {
          feeds[item.id] = item.feeds
        }
        setFeederMap(feeds)
      } else {
        setLoadError('Не удалось загрузить распределение фаз')
        setPdus([])
        setCapacity(null)
        setFeederMap({})
      }
      if (spofRes.status === 'fulfilled') setSpof(spofRes.value.data)
    } finally {
      setLoading(false)
    }
  }, [code])

  useEffect(() => { loadAll() }, [loadAll])

  if (loading) return <div className="p-6 text-text-muted">Загрузка...</div>

  const criticalIssues = spof?.issues.filter(i => i.severity === 'critical') || []

  return (
    <div className="p-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Распределение фаз</h1>
          <p className="text-text-secondary text-sm mt-1">
            PDU, фазы, нагрузка, SPOF-анализ
          </p>
        </div>
        <select
          value={code}
          onChange={e => setCode(e.target.value)}
          className="text-sm bg-bg-input border border-border rounded-sm px-3 py-1.5"
        >
          {dcList.map(dc => (
            <option key={dc.code} value={dc.code}>{dc.name || dc.code}</option>
          ))}
        </select>
      </div>

      {/* Capacity Overview */}
      {capacity && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader title="Общая мощность" />
            <div className="p-3">
              <div className="text-2xl font-bold font-mono">{Math.round(capacity.total_capacity_watts / 1000).toLocaleString()} kW</div>
              <div className="text-xs text-text-secondary mt-1">
                Потреблено: {Math.round(capacity.total_consumption_watts / 1000).toLocaleString()} kW
              </div>
            </div>
          </Card>
          <Card>
            <CardHeader title="Резерв" />
            <div className="p-3">
              <div className={`text-2xl font-bold font-mono ${capacity.headroom_pct > 30 ? 'text-status-normal' : capacity.headroom_pct > 15 ? 'text-status-warning' : 'text-status-critical'}`}>
                {Math.round(capacity.headroom_pct)}%
              </div>
              <div className="text-xs text-text-secondary mt-1">
                {Math.round(capacity.headroom_watts / 1000).toLocaleString()} kW свободно
              </div>
            </div>
          </Card>
          <Card>
            <CardHeader title="Стойки с питанием" />
            <div className="p-3">
              <div className="text-2xl font-bold font-mono">{capacity.racks_with_power}/{capacity.total_racks}</div>
              <div className="text-xs text-text-secondary mt-1">стоек подключено</div>
            </div>
          </Card>
          <Card>
            <CardHeader title="Баланс фаз" />
            <div className="p-3 space-y-1">
              {Object.entries(capacity.phase_imbalance || {}).map(([phase, val]) => (
                <div key={phase} className="flex justify-between text-sm">
                  <span className="text-text-secondary">{phase}</span>
                  <span className="font-mono">{Math.round(val)} W</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* SPOF Alerts */}
      {criticalIssues.length > 0 && (
        <div className="mb-6 bg-status-critical/10 border border-status-critical/30 rounded-sm p-4">
          <h3 className="text-status-critical font-semibold mb-2">⚠ Критические риски SPOF</h3>
          {criticalIssues.map((issue, i) => (
            <div key={i} className="text-sm text-text-secondary mb-1">
              • {issue.description}
              {issue.affected.length > 0 && (
                <span className="text-text-muted"> ({issue.affected.slice(0, 3).join(', ')})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rack Power Breakdown */}
      {capacity && ((capacity.rack_breakdown && capacity.rack_breakdown.length > 0) || (capacity.rack_consumption && Object.keys(capacity.rack_consumption).length > 0)) && (
        <Card className="mb-6">
          <CardHeader title="Нагрузка по стойкам (NetBox / БД / Телеметрия)" />
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {capacity.rack_breakdown && capacity.rack_breakdown.length > 0
                ? capacity.rack_breakdown.map(r => {
                    const watts = r.consumption_watts
                    const pct = r.max_power_w > 0 ? Math.round((watts / r.max_power_w) * 100) : 0
                    return (
                      <div key={r.rack_id} className="bg-bg-panel border border-border rounded-sm p-2.5 flex flex-col justify-between">
                        <div>
                          <div className="text-xs font-semibold text-text-primary truncate" title={r.label}>
                            {r.label}
                          </div>
                          <div className="text-[10px] text-text-muted">
                            Ряд {r.row_index + 1} · Поз. {r.rack_index + 1}
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="text-base font-bold font-mono text-text-primary">
                            {watts >= 1000 ? `${(watts / 1000).toFixed(2)} kW` : `${Math.round(watts)} W`}
                          </div>
                          <div className="text-[10px] text-text-secondary mt-0.5 flex items-center justify-between">
                            <span className={pct > 80 ? 'text-status-critical font-medium' : pct > 60 ? 'text-status-warning' : 'text-text-secondary'}>
                              {watts > 0 ? `${pct}%` : 'Холостой'}
                            </span>
                            <span className="text-text-muted font-mono">{Math.round(r.max_power_w / 1000)} kW макс</span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                : Object.entries(capacity.rack_consumption || {}).map(([rackId, watts]) => (
                    <div key={rackId} className="bg-bg-panel border border-border rounded-sm p-2.5 flex flex-col justify-between">
                      <div className="text-xs text-text-muted">Стойка #{rackId}</div>
                      <div className="text-base font-bold font-mono text-text-primary mt-1">
                        {watts >= 1000 ? `${(watts / 1000).toFixed(2)} kW` : `${Math.round(watts)} W`}
                      </div>
                      <div className="text-[10px] text-text-secondary mt-0.5">
                        {watts > 0 ? 'Нагрузка активна' : 'Холостой ход'}
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </Card>
      )}

      {/* PDU Grid */}
      <Card>
        <CardHeader title="PDU сетка" />
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loadError ? (
            <div className="col-span-full text-center text-text-muted py-12">{loadError}</div>
          ) : pdus.length === 0 ? (
            <div className="col-span-full text-center text-text-muted py-12">
              Нет данных о PDU
            </div>
          ) : (
            pdus.map(pdu => (
              <PduVisual key={pdu.id} pdu={pdu} feeds={feederMap[pdu.id] || []} />
            ))
          )}
        </div>
      </Card>
    </div>
  )
}
